import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { AdminSettingsDataAccessService } from '@flexyoo/admin/settings/data-access';
import {
  ApplicationPlanType,
  CartManagementMode,
  EmployeeUserAgreementDto,
  UserAgreementType,
} from '@flexyoo/api/models';
import { EmployeeApiService, TenantGroupGeneralPolicyApiService } from '@flexyoo/api/services';
import { AuthenticationService } from '@flexyoo/auth';
import { FinancialImpactDataAccessService } from '@flexyoo/employee/data-access';
import { employeeSettingsSelectors } from '@flexyoo/employee/store';
import { isCafeteriaPlanEnrolled, isMobilityBudgetEnrolled } from '@flexyoo/employee/util';
import { environment } from '@flexyoo/environment';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { downloadURI } from '@flexyoo/shared/functions';
import { FilesDataAccessService } from '@flexyoo/shared/services';
import { TenantGroupPeriodDataAccessService } from '@flexyoo/tenant-group/data-access';
import { FinancialImpactReportPeriod } from '@flexyoo/tenant-group/models';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';
import { AgreementSignPageComponent } from './agreement-sign-page/agreement-sign-page.component';

type FinancialImpactReportListItem = FinancialImpactReportPeriod & {
  isDownloading?: boolean;
};

/**
 * The signed agreement in force and the archived ones are shaped differently by
 * the API, so both are narrowed to what a document row needs to render.
 */
type ISignedAgreement = {
  Id: number;
  AppFileId?: number;
  ApprovedDate?: Date | string;
};

type IUserAgreementListItem = {
  id: number;
  appFileId: number;
  year: number | null;
  fileName: string;
};

@Component({
  selector: 'app-employee-account-page',
  templateUrl: './employee-account-page.component.html',
  styleUrl: './employee-account-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NgOnDestroy],
  imports: [
    MatTabsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    AsyncPipe,
    AgreementSignPageComponent,
    FlexyooLetDirective,
    MatCard,
    MatCardTitle,
    MatCardContent,
    DatePipe,
    MatProgressSpinner,
  ],
})
export class EmployeeAccountPageComponent {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthenticationService);
  private readonly adminSettingsService = inject(AdminSettingsDataAccessService);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly filesService = inject(FilesDataAccessService);
  private readonly tenantGroupPeriodService = inject(TenantGroupPeriodDataAccessService);
  private readonly financialImpactService = inject(FinancialImpactDataAccessService);
  private readonly tenantGroupGeneralPolicyApi = inject(TenantGroupGeneralPolicyApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly ApplicationPlanType = ApplicationPlanType;

  protected readonly isHrGroupAdmin$ = this.authService.isUserGroupAdmin$;

  private readonly employeeSettings$ = this.store.select(
    employeeSettingsSelectors.selectEmployeeSettings
  );

  protected readonly isAgreementSigningTabActive$ = combineLatest([
    this.store.select(tenantSettingsSelectors.selectTenantSettings),
    this.employeeSettings$,
  ]).pipe(
    map(([tenantSettings, employeeSettings]) => {
      const needsCafeteriaAgreement =
        isCafeteriaPlanEnrolled(employeeSettings) && !!tenantSettings?.useCafeteriaAgreement;
      const needsMobilityAgreement =
        isMobilityBudgetEnrolled(employeeSettings) && !!tenantSettings?.useMobilityBudgetAgreement;

      return needsCafeteriaAgreement || needsMobilityAgreement;
    }),
    distinctUntilChanged()
  );

  protected readonly isCafeteriaPlanEnrolled$ = this.employeeSettings$.pipe(
    map((settings) => isCafeteriaPlanEnrolled(settings)),
    distinctUntilChanged()
  );

  protected readonly isMobilityBudgetPlan$ = this.employeeSettings$.pipe(
    map((settings) => !!settings?.isMobilityBudgetFinancingPlan),
    distinctUntilChanged()
  );

  protected readonly userLanguage$ = this.authService.currentUserSubject.pipe(
    map((user) => user?.Language)
  );

  protected readonly staticEmployeeUserGuide$ = this.userLanguage$.pipe(
    map((userLanguage) =>
      userLanguage
        ? `${environment.static}/instructions/User guide Employee ${userLanguage.toUpperCase()}.pdf`
        : '#'
    )
  );

  /** Every agreement the employee has already signed, both plans mixed together. */
  private readonly archivedAgreements$: Observable<EmployeeUserAgreementDto[]> =
    this.employeeSettings$.pipe(
      map((settings) => settings?.id),
      distinctUntilChanged(),
      switchMap((id) =>
        id
          ? this.employeeApi.userAgreementList(id, { WithArchived: true }).pipe(
              map((a) => a.filter((x) => x.IsArchived)),
              catchError(() => of([]))
            )
          : of([])
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  protected readonly cafeteriaAgreements$: Observable<IUserAgreementListItem[]> = combineLatest([
    this.employeeSettings$,
    this.archivedAgreements$,
  ]).pipe(
    map(([settings, archived]) =>
      this.toAgreementList(
        settings?.cafeteriaUserAgreement,
        archived.filter((agreement) => agreement.Type !== UserAgreementType.MobilityBudget),
        'user-agreement.pdf'
      )
    )
  );

  protected readonly mobilityBudgetAgreements$: Observable<IUserAgreementListItem[]> =
    combineLatest([this.employeeSettings$, this.archivedAgreements$]).pipe(
      map(([settings, archived]) =>
        this.toAgreementList(
          settings?.mobilityBudgetUserAgreement,
          archived.filter((agreement) => agreement.Type === UserAgreementType.MobilityBudget),
          'lmb-user-agreement.pdf'
        )
      )
    );

  /**
   * One stored report per choice period the employee took part in.
   * Temporary (50857): the report is only offered to clients with cart logic.
   */
  protected readonly financialImpactReports$: Observable<FinancialImpactReportListItem[]> =
    this.store.select(tenantSettingsSelectors.selectCartManagementMode).pipe(
      map((mode) => mode === CartManagementMode.Cart),
      distinctUntilChanged(),
      switchMap((isCartMode) =>
        isCartMode
          ? this.tenantGroupPeriodService
              .getEmployeeReportHistoryPeriods()
              .pipe(catchError(() => of([])))
          : of([])
      )
    );

  protected readonly isFinancialImpactExpanded = signal(false);

  protected toggleFinancialImpact(): void {
    this.isFinancialImpactExpanded.update((expanded) => !expanded);
  }

  protected downloadUserAgreement(agreement: IUserAgreementListItem): void {
    this.filesService
      .downloadFileById(agreement.appFileId)
      .subscribe((response: HttpResponse<Blob>) => {
        if (response?.body) {
          const url = window.URL.createObjectURL(response.body);
          downloadURI(url, agreement.fileName);
        }
      });
  }

  protected downloadFinancialImpactReport(report: FinancialImpactReportListItem): void {
    report.isDownloading = true;
    this.financialImpactService
      .downloadReportPdf({ TenantGroupPeriodId: report.periodId })
      .pipe(
        finalize(() => {
          report.isDownloading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((response: HttpResponse<Blob>) => {
        if (response?.body) {
          const url = window.URL.createObjectURL(response.body);
          downloadURI(url, `FinancialImpactReport_${report.year}_${report.name}.pdf`, false);
        }
      });
  }

  protected onGeneralPolicyDownload(lang: string, type: ApplicationPlanType): void {
    const tenantGroupId = this.authService.getCurrentUser()?.TenantGroupId;
    if (!tenantGroupId) {
      return;
    }

    this.tenantGroupGeneralPolicyApi
      .getFile(tenantGroupId, lang, { applicationPlanType: type })
      .subscribe((response: HttpResponse<Blob>) => {
        if (response?.body) {
          const url = window.URL.createObjectURL(response.body);
          downloadURI(url, response.headers.get('x-file-name'), false);
        }
      });
  }

  protected onPrivacyStatement(lang: string): void {
    this.adminSettingsService
      .downloadPrivacyStatement(lang)
      .subscribe((response: HttpResponse<Blob>) => {
        if (response) {
          const url = window.URL.createObjectURL(response.body);
          downloadURI(url, 'PrivacyStatement', false);
        }
      });
  }

  /**
   * The agreement in force first, then the archived ones by descending year — an
   * agreement that has been uploaded but not signed yet carries no year and
   * leads the list.
   */
  private toAgreementList(
    current: ISignedAgreement | undefined,
    archived: ISignedAgreement[],
    fileNameSuffix: string
  ): IUserAgreementListItem[] {
    return [...(current ? [current] : []), ...archived]
      .filter((agreement) => !!agreement.AppFileId)
      .map((agreement) => {
        const year = agreement.ApprovedDate ? new Date(agreement.ApprovedDate).getFullYear() : null;

        return {
          id: agreement.Id,
          appFileId: agreement.AppFileId,
          year,
          fileName: year ? `${year}-${fileNameSuffix}` : fileNameSuffix,
        };
      })
      .sort((a, b) => (b.year ?? Infinity) - (a.year ?? Infinity));
  }
}
